// Shared helpers for grouping workspace resources by their section.
//
// `section` and `type` are free-form strings on WorkspaceResource, so a studio
// can create custom sections beyond the built-in framework (know/say/show/do/
// training). These helpers surface those custom sections everywhere resources
// are displayed — the admin library, the playbook, and the broadcast picker —
// so the same grouping logic lives in exactly one place.
//
// Section matching is case-insensitive throughout: built-in sections are stored
// lowercase, but a resource could carry stray casing (e.g. "Know"), and custom
// sections that differ only in case ("Marketing" vs "marketing") are treated as
// one section rather than duplicated.

// Resources with a blank section fall into this bucket.
export const FALLBACK_RESOURCE_SECTION = "other";

// Custom section/type names are short organizational labels — they render as
// sidebar nav items, group headers, badges, and select options. Cap their
// length so a pathological value can't break those layouts or bloat payloads.
// The longest built-in value ("Company Decks & Information") is 27 chars, so 60
// leaves generous headroom for custom names. Enforced on the client (the
// CreatableCombobox input's maxLength) and authoritatively on the server (the
// resources `add` route).
export const MAX_RESOURCE_TAXONOMY_LENGTH = 60;

// The list of sections to render, in order: the built-in sections first, then
// any custom sections found on the resources (each surfaced by its own name),
// and finally an "other" bucket only if some resources have a blank section.
export function getResourceDisplaySections(resources, sections) {
  const known = new Set(sections.map((section) => section.toLowerCase()));
  const customSections = [];
  const seenCustom = new Set();
  let hasFallback = false;

  resources.forEach((resource) => {
    const raw = (resource.section || "").trim();
    const lowered = raw.toLowerCase();
    // Blank sections (and the literal "other" name) share the fallback bucket.
    if (!lowered || lowered === FALLBACK_RESOURCE_SECTION) {
      hasFallback = true;
      return;
    }
    // Built-ins render in their own curated blocks; custom sections are added
    // once (case-insensitively), keeping the casing the user first typed.
    if (known.has(lowered) || seenCustom.has(lowered)) return;
    seenCustom.add(lowered);
    customSections.push(raw);
  });

  customSections.sort((a, b) => a.localeCompare(b));

  const ordered = [...sections, ...customSections];
  if (hasFallback) ordered.push(FALLBACK_RESOURCE_SECTION);
  return ordered;
}

// Build a case-insensitive lookup from a section name to its canonical display
// label, so resources with stray casing still resolve to the right group.
export function buildSectionLookup(displaySections) {
  const lookup = new Map();
  displaySections.forEach((section) => {
    lookup.set(section.toLowerCase(), section);
  });
  return lookup;
}

// Resolve which display section a resource belongs to, given a lookup built
// from the display sections that actually exist (built-in + custom). Blank or
// unknown sections → fallback.
export function resolveResourceSection(resource, sectionLookup) {
  const raw = (resource.section || "").trim();
  const match = raw && sectionLookup.get(raw.toLowerCase());
  return match || FALLBACK_RESOURCE_SECTION;
}

// Group resources into { section -> resources[] } using the display sections
// above. Returns the grouping plus the ordered section list for rendering.
export function groupResourcesBySection(resources, sections) {
  const displaySections = getResourceDisplaySections(resources, sections);
  const sectionLookup = buildSectionLookup(displaySections);
  const grouped = {};

  displaySections.forEach((section) => {
    grouped[section] = [];
  });

  resources.forEach((resource) => {
    const section = resolveResourceSection(resource, sectionLookup);
    if (grouped[section]) grouped[section].push(resource);
  });

  return { grouped, displaySections };
}

// Put a flat list of resources in the order the admin content library shows
// them: section (built-ins, then custom, then "other"), then type group by the
// studio's typeOrder, then each resource's own `order`. Lets a flat list, such
// as the home page's featured resources, follow the sequence an admin laid out
// by dragging. Ties keep their incoming order (Array#sort is stable).
export function sortResourcesInLibraryOrder(resources, sections) {
  const list = resources || [];
  const displaySections = getResourceDisplaySections(list, sections);
  const sectionLookup = buildSectionLookup(displaySections);
  const sectionRank = new Map(
    displaySections.map((section, index) => [section, index]),
  );

  // A type group sorts by its lowest typeOrder, like the admin grouping does.
  const typeRank = new Map();
  const typeKey = (resource) =>
    `${resolveResourceSection(resource, sectionLookup)}\u0000${resource.type || "other"}`;
  const typeOrderOf = (resource) =>
    typeof resource.typeOrder === "number" ? resource.typeOrder : 999;
  list.forEach((resource) => {
    const key = typeKey(resource);
    const order = typeOrderOf(resource);
    if (!typeRank.has(key) || order < typeRank.get(key)) {
      typeRank.set(key, order);
    }
  });

  return [...list].sort(
    (a, b) =>
      sectionRank.get(resolveResourceSection(a, sectionLookup)) -
        sectionRank.get(resolveResourceSection(b, sectionLookup)) ||
      typeRank.get(typeKey(a)) - typeRank.get(typeKey(b)) ||
      typeKey(a).localeCompare(typeKey(b)) ||
      (a.order || 0) - (b.order || 0),
  );
}

// Resources with a blank type fall into this group (matches the playbook's
// historical "Other" heading).
export const FALLBACK_RESOURCE_TYPE = "Other";

// Slug for an anchor id derived from a free-form section/type name. Never
// empty, so a name made entirely of punctuation still gets a usable anchor.
export function slugifyResourceTaxonomy(value) {
  const slug = String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "group";
}

// Separator between a playbook section anchor and one of its type-group
// anchors. Section ids only ever contain single hyphens (see the custom-section
// slugs in SalesPlaybook), so a double hyphen unambiguously marks the split.
export const RESOURCE_TYPE_ANCHOR_SEPARATOR = "--";

// Anchor id for the "<type>" heading inside the playbook section whose own
// anchor is `sectionAnchorId`, e.g. our-content-know--training-materials.
export function buildResourceTypeAnchorId(sectionAnchorId, type) {
  return `${sectionAnchorId}${RESOURCE_TYPE_ANCHOR_SEPARATOR}${slugifyResourceTaxonomy(type)}`;
}

const RESOURCE_ANCHOR_PREFIX = "resource-";

// Anchor id for one resource's row in the playbook. Sabine's answers link to
// it, and following the link opens that row (SAL-3285).
export function buildResourceAnchorId(resourceId) {
  return `${RESOURCE_ANCHOR_PREFIX}${resourceId}`;
}

// The resource a row anchor names, or null for any other anchor.
export function getResourceIdFromAnchor(anchorId) {
  const value = String(anchorId || "");
  return value.startsWith(RESOURCE_ANCHOR_PREFIX)
    ? value.slice(RESOURCE_ANCHOR_PREFIX.length) || null
    : null;
}

// Given any playbook anchor (section or type-group), the section anchor that
// owns it — so a link to a subheading can expand its parent section.
export function getParentSectionAnchorId(anchorId) {
  const index = String(anchorId || "").indexOf(RESOURCE_TYPE_ANCHOR_SEPARATOR);
  return index === -1 ? anchorId : anchorId.slice(0, index);
}

// The type groups a list of (already section-filtered) resources renders as,
// in display order: [{ type, count, typeOrder }]. Free-form `type` values are
// grouped exactly as typed, ordered by the studio's typeOrder, then by name so
// two groups sharing an order are stable. Shared by the playbook resource list
// (which renders the group headings) and the playbook sidebar (which lists
// them as subheadings), so the two can never disagree.
export function getResourceTypeGroups(resources) {
  const groups = new Map();
  (resources || []).forEach((resource) => {
    const type = (resource?.type || "").trim() || FALLBACK_RESOURCE_TYPE;
    const order =
      typeof resource?.typeOrder === "number" ? resource.typeOrder : 999;
    const existing = groups.get(type);
    if (existing) {
      existing.count += 1;
      existing.typeOrder = Math.min(existing.typeOrder, order);
    } else {
      groups.set(type, { type, count: 1, typeOrder: order });
    }
  });
  return Array.from(groups.values()).sort(
    (a, b) => a.typeOrder - b.typeOrder || a.type.localeCompare(b.type),
  );
}
